import React from 'react';
import {
  ToggleButton,
  ChoiceList,
  TextField,
  Heading,
  DisplayText,
  Checkbox,
  Dropdown
} from '@superops/ui-design-system';
import { getTranslatedText } from '../index';
import { useIntl } from 'react-intl';
import { FORM_FIELDS_TYPE } from '../Constants';
import { SVGIcon } from '@superops/super-icons';
import { FormattedMessage } from 'react-intl';
import { SuperPop } from '@nile/components';
import type { MdmPasswordContainerProps, MdmPasswordField } from '../types/interface';

function MdmPasswordContainer(props: MdmPasswordContainerProps) {
  const {
    formValues,
    onFormValuesChange,
    fieldsFromMeta,
    formErrors,
    highlightedAttributeId,
    header
  } = props;
  const intl = useIntl();

  const renderTextField = (field: MdmPasswordField, isDisabled: boolean) => {
    return (
      <>
        <div
          className={`flex-item-fixed ${highlightedAttributeId ===
            field.attributeId && 'cosmic-latter'}`}
        >
          <div className={`flex-row mb-8 ${field?.autoWipe && 'autowipe d-in pr70'}`}>
            <div className="flex-item-fixed field-label mr-20 flex actr min-width-300">
              <DisplayText element="p" size="large" weight="Medium">
                <FormattedMessage id={field?.uiLabel} />
              </DisplayText>
            </div>
            <div className="flex-item-fixed min-w200">
              <TextField
                placeholder={getTranslatedText(
                  intl,
                  field.placeholder
                )}
                value={String(formValues?.[field.key] ?? '')}
                disabled={isDisabled}
                onChange={(val: string) => {
                  onFormValuesChange({
                    ...formValues,
                    [field.key]: val ? Number(val) : null
                  });
                }}
                type={field.dataType}
                suffix={
                  getTranslatedText(intl, field.suffix)
                }
                error={formErrors[field.key]}
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderRadio = (field: MdmPasswordField, isDisabled: boolean) => {
    const choices = (field?.uiOptions || []).map((opt) => ({
      key: opt.key,
      label: (
        <div className="flex actr g5">
          <div><FormattedMessage id={opt?.uiLabel} /></div>
          {
            opt?.additionalInfo && (
              <SuperPop
                position="bottom-start"
                trigger="mouseenter"
                content= {
                  getTranslatedText(intl, opt.additionalInfo)
                }
                noHeader={true}
              />
            )
          }

        </div>
      ),
      value: opt.value
    }));

    const selectedOption = choices.find((c) => (formValues?.[c.key]));
    const selectedValue = selectedOption
      ? selectedOption.value
      : field?.uiDefaultValue;

    return (
      <div
        className={`flex actr mb10 ${highlightedAttributeId ===
          field.attributeId && 'cosmic-latter'}`}
      >
        <div className="border-right-gray pr-20">
          <DisplayText element="p" size="large" weight="Medium">
            <FormattedMessage id={field?.uiLabel} />
          </DisplayText>
        </div>
        <div className="ml-10">
          <ChoiceList
            choices={choices}
            horizontal
            disabled={isDisabled}
            onChange={(values: string[]) => {
              if (field?.dataType === 'Boolean') {
                const selected = Array.isArray(values) ? values[0] : values;
                const updatedValues: Record<string, boolean> = {};
                (field.uiOptions || []).forEach((opt) => {
                  updatedValues[opt.key] = opt.key === selected;
                });
                onFormValuesChange({ ...formValues, ...updatedValues });
              } else if (field?.dataType === 'String') {
                onFormValuesChange({ ...formValues, [field.key]: values[0] });
              }
            }}
            selected={
              field?.dataType === 'String'
                ? formValues?.[field.key]
                  ? [formValues?.[field.key]]
                  : []
                : selectedValue
                ? [selectedValue]
                : []
            }
          />
        </div>
      </div>
    );
  };

  const renderDropdown = (field: MdmPasswordField, isDisabled: boolean) => {
    const choices = (field?.uiOptions || []).map((opt) => ({
      key: opt.key,
      label:opt.label,
      value: opt.value
    }));
    const selectedOption = choices.find((c) => (formValues?.[field.key] === c.value));
      return (
        <div className="flex-row mb-8">
           <div className="flex-item-fixed field-label mr-20 flex actr min-width-300">
           <DisplayText element="p" size="large" weight="Medium">
                <FormattedMessage id={field?.uiLabel} />
          </DisplayText>
          </div>
          <div className="flex-item-fixed min-w200">
          <Dropdown
            value={selectedOption}
            onChange={(value: string) => {
              onFormValuesChange({ ...formValues, [field.key]: value?.value });
            }}
            options={choices}
            disabled={isDisabled}
            styles={{
              menuPortal: (base: React.CSSProperties) => ({
                ...base,
                zIndex: 9999
              }),
              menu: (base: React.CSSProperties) => ({ ...base, zIndex: 9999 })
            }}
            menuPlacement="auto"
            menuPosition="fixed"
          />
          </div>

        </div>
      )
  }

  const renderCheckbox = (field: MdmPasswordField, isDisabled: boolean) => {
    return (
      <div
        className={`mb-15 flex astart actr ${highlightedAttributeId ===
          field.attributeId && 'cosmic-latter'}`}
      >
        <Checkbox
          checked={formValues?.[field.key]}
          disabled={isDisabled}
          onChange={(value: boolean) => {
            onFormValuesChange({ ...formValues, [field.key]: value });
          }}
        />

        <div className="ml8">
          <DisplayText element="p" size="large" weight="Medium">
            <FormattedMessage id={field?.uiLabel} />
          </DisplayText>
        </div>
      </div>
    );
  };

  const renderGroupTextField = (field: MdmPasswordField, isDisabled: boolean) => {
    const children = field?.children || [];
    return (
      <div
        className={`flex-item-fixed ${highlightedAttributeId ===
          field.attributeId && 'cosmic-latter'}`}
      >
        <div className={`flex-row mb-8 ${field?.autoWipe && 'autowipe'}`}>
          <div className="flex-col w-100">
            <div className="flex-item-fixed field-label mr-20 flex actr min-width-300 mb-10">
              <SVGIcon name="WarningTriangleOutline" color=" #bf0711" />
              <Heading element="h3" weight="Bold">
                <div className="ml-10 error">
                  <FormattedMessage id={field?.header} />
                  </div>
              </Heading>
            </div>
            {children.map((child) => (
              <div key={child.key}>
                {renderTextField(
                  {
                    ...child,
                    isDisabled: isDisabled,
                    onChange: (val: string) => {
                      const updated = {
                        ...formValues,
                        [child.key]: Number(val)
                      };
                      if (
                        Array.isArray(field?.children) &&
                        field.children.length
                      ) {
                        const values = field.children.map((c) =>
                          Number(updated[c.key] ?? 0)
                        );
                        const numericValues = values.filter((n) =>
                          Number.isFinite(n)
                        );
                        updated[field.key] = numericValues.length
                          ? Math.max(...numericValues)
                          : null;
                      }
                      onFormValuesChange(updated);
                    }
                  } as MdmPasswordField,
                  isDisabled
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderToggle = (field: MdmPasswordField, isDisabled: boolean) => {
    return (
      <div
        className={`mb-15 flex astart actr ${highlightedAttributeId ===
          field.attributeId && 'cosmic-latter'}`}
      >
        <ToggleButton
          value={formValues?.[field.key]}
          size="small"
          locked={isDisabled}
          onChange={(value: boolean) => {
            if (field?.key) {
              onFormValuesChange({
                ...formValues,
                [field.key]: !!value
              });
            }
          }}
        />
        <div className="ml8 mr-5">
          <DisplayText element="p" size="large" weight="Medium">
            <FormattedMessage id={field?.uiLabel} />
          </DisplayText>
        </div>
        {
            field?.additionalInfo && (
              <SuperPop
                position="bottom-start"
                trigger="mouseenter"
                content= {
                  getTranslatedText(intl, field.additionalInfo)
                }
                noHeader={true}
              />
            )
          }
      </div>
    );
  };

  const renderByType = (field: MdmPasswordField) => {
    const isDisabled =
      field?.key !== field?.controllingFieldKey &&
      (formValues?.[field?.controllingFieldKey as string] as boolean | undefined) === false;
    switch (field?.uiDataType) {
      case FORM_FIELDS_TYPE.TOGGLE:
        return <div key={field.key}>{renderToggle(field, isDisabled)}</div>;
      case FORM_FIELDS_TYPE.TEXT:
        return <div key={field.key}>{renderTextField(field, isDisabled)}</div>;
      case FORM_FIELDS_TYPE.SELECT:
        return <div key={field.key}>{renderRadio(field, isDisabled)}</div>;
      case FORM_FIELDS_TYPE.GROUP_TEXT:
        return (
          <div key={field.key}>{renderGroupTextField(field, isDisabled)}</div>
        );
      case FORM_FIELDS_TYPE.CHECKBOX:
        return <div key={field.key}>{renderCheckbox(field, isDisabled)}</div>;
      case FORM_FIELDS_TYPE.DROP_DOWN:
        return <div key={field.key}>{renderDropdown(field, isDisabled)}</div>;
      default:
        return null;
    }
  };

  return (
      <div>{fieldsFromMeta?.sections?.map((field) => renderByType(field as MdmPasswordField))}</div>
  );
}

export default MdmPasswordContainer;
